import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import _ from 'lodash';

import ToastAx        from 'app/actions/toast';
import EllipsisMenu   from 'app/components/common/ellipsis-menu';
import Icon           from 'app/components/common/icon';
import Modal          from 'app/components/common/modal';
import MultiEmpSelect from 'app/components/company-admin/common/multi-emp-select';
import {
  EmployeeStatuses,
}                     from 'app/constants';
import GrantSysDuck   from 'app/ducks/company-admin/grant-system';
import helpers        from 'app/helpers/grant-system';
import history        from 'app/history';
import paths          from 'app/paths';
import prompts        from 'app/prompts';
import AuthSlx        from 'app/selectors/auth';

const {
  StageTypes,
  TaskTypes,
  AppStatuses,
} = helpers;

const showStage = (app, stage) => {
  const isApprovedStage = stage.type === StageTypes.SYSTEM_APPROVED;
  const isDeclinedStage = stage.type === StageTypes.SYSTEM_DECLINED;
  if (!isApprovedStage && !isDeclinedStage) return true;
  const isAppApproved = app?.status === AppStatuses.APPROVED;
  const isAppDeclined = app?.status === AppStatuses.DECLINED;
  if (isDeclinedStage) return isAppDeclined;
  return !isAppDeclined;
};

const renderAssResultChip = (ass) => {
  const isComplete = !!ass.completedTs;
  if (!isComplete) return null;
  const taskType = ass.resultTaskDetails?.type || null;
  const resultVal = (ass.result || {})[taskType];
  if (resultVal == null) return null;

  const isScore   = taskType === TaskTypes.SCORE;
  const isPos     = !!(!isScore && (resultVal > 0));
  const isNeg     = !!(!isScore && (resultVal < 0));
  
  const color = (() => {
    if (isPos) return 'green';
    if (isNeg) return 'danger';
    if (isScore) return 'blue';
    return 'gray';
  })();

  const label = (() => {
    if (taskType === TaskTypes.PASS_FAIL) return isPos ? 'Pass' : 'Fail';
    if (taskType === TaskTypes.VOTE) {
      if (resultVal === -2) return 'Strong No';
      if (resultVal === -1) return 'No';
      if (resultVal ===  0) return 'Neutral';
      if (resultVal ===  1) return 'Yes';
      if (resultVal ===  2) return 'Strong Yes';
    }
    if (taskType === TaskTypes.SCORE) return `${resultVal}`;
    if (taskType === TaskTypes.FINAL) return isPos ? 'Approved' : 'Declined';
    if (taskType === TaskTypes.PAYMENT) return isPos ? 'Paid' : '';
    return '';
  })();

  return <div className={`ca-gapp-checks-task-todo-res ${color}`}>{label}</div>
};

class CadminGrantAppChecklist extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      showAssigneesModal: null,
      modalNewEmpIds: [],
      modalSavePending: false,
      addSelfPending: false,
    };

    this.onChangeNewEmpIds = this.onChangeNewEmpIds.bind(this);
    this.onCloseAssigneesModal = this.onCloseAssigneesModal.bind(this);
    this.onClickAddAssignees = this.onClickAddAssignees.bind(this);
  }

  getAssignments(task) {
    return (this.props.assignments || []).filter((ass) => {
      if (ass.grantTaskId !== task.id) return false;
      const isEmpDeactivated = ass.employee?.status === EmployeeStatuses.DEACTIVATED;
      const isComplete = !!ass.completedTs;
      if (isEmpDeactivated && !isComplete) return false;
      return true;
    }).sort((a, b) => {
      if (!a.completedTs && !!b.completedTs) return 1;
      if (!!a.completedTs && !b.completedTs) return -1;
      return 0;
    });
  }

  onClickAssigneesModal(stage, task) {
    this.setState({showAssigneesModal: [stage.id, task.id]});
  }
  onCloseAssigneesModal() {
    this.setState({showAssigneesModal: null, modalNewEmpIds: []});
  }

  async onClickAddMyself(stage, task) {
    const employeeIds = [this.props.currentUser.employment.employee.id];
    this.setState({addSelfPending: true});
    const {grantAssignmentIds} = await this.props.createAssignments(task.id, employeeIds);
    const path = paths.cadminGrantAssignment(this.props.company.slug, grantAssignmentIds[0], {backTo: 'app'});
    history.push(path);
    this.setState({addSelfPending: false});
  }

  onChangeNewEmpIds(mesComp) {
    const modalNewEmpIds = mesComp.selectedEmployeeIds;
    this.setState({modalNewEmpIds})
  }
  async onClickAddAssignees() {
    const {showAssigneesModal, modalNewEmpIds: employeeIds} = this.state;
    const [stageId, taskId] = showAssigneesModal;
    // const params = {employeeIds, taskId};
    this.setState({modalSavePending: true});
    await this.props.createAssignments(taskId, employeeIds);
    this.props.toastSuccess('New assignees added.');
    this.setState({modalSavePending: false, showAssigneesModal: null});
  }

  async onClickDeleteAssignment(ass, employee, task, event) {
    event.preventDefault();
    const msg = `Remove ${employee.firstName} ${employee.lastName} from ${task.title}?`;
    const didConfirm = await prompts.confirm({msg, danger: 'Remove Assignee'});
    if (didConfirm) {
      this.props.deleteAssignment(ass.id);
    }
  }

  renderAssigneesModal() {
    const {showAssigneesModal, modalNewEmpIds, modalSavePending} = this.state;
    if (!showAssigneesModal) return null;
    const {workflow, app} = this.props;
    const [stageId, taskId] = showAssigneesModal;
    const stage = workflow.stages.find(s => s.id === stageId);
    const task = stage.tasks.find(t => t.id === taskId);
    const btnDisabled = (() => {
      if (!modalNewEmpIds.length) return true;
      if (modalSavePending) return true;
      return false;
    })();

    return (
      <Modal className="bform" onClose={this.onCloseAssigneesModal}>
        <h1 className="bform-h1">Add Assignees</h1>

        <label className="bform-h3">Application</label>
        <p>{`Application ${app.number}`}</p>

        <label className="bform-h3">Stage / Task</label>
        {`${stage.title} / ${task.title}`}

        <label className="bform-h3">New Assignees</label>
        <MultiEmpSelect onChange={this.onChangeNewEmpIds} />

        <div className="bform-actions">
          <div className="bform-actions-right">
            <button className="btn blue" onClick={this.onClickAddAssignees} disabled={btnDisabled}>{modalSavePending ? 'Adding...' : 'Add Assignees'}</button>
          </div>
        </div>
      </Modal>
    );
  }

  renderWorkflow() {
    const {app, workflow, assignments: allAssigns} = this.props;
    if (!workflow) return 'loading...';
    const currentStage = workflow.stages.find(stage => stage.id === app?.currentGrantStageId);
    let isPast = true;
    let isFuture = false;
    let _nextIsFuture = false;

    return (
      <div className="ca-gapp-checks-wf">
        {workflow.stages.map((stage, si) => {
          const isCurrent = stage.id === app?.currentGrantStageId;
          isFuture = _nextIsFuture;
          if (isCurrent) {
            isPast = false;
            _nextIsFuture = true;
          }
          if (!showStage(app, stage)) return null;
          const isStageComplete = !!(currentStage && isPast);
          const isDeclinedStage = stage.type === StageTypes.SYSTEM_DECLINED;
          const stageNum = isDeclinedStage ? si : (si + 1);
          const stageAssignment = app?.stageAssignments.find(sass => sass.stageId === stage.id);
          const reachedClass = stageAssignment ? 'reached' : 'not-reached';
          const stageTimeClass = (() => {
            if (isCurrent) return 'current';
            if (isPast) return 'past';
            return 'future';
          })();

          return (
            <div key={stage.id} className={`ca-gapp-checks-stage ${stageTimeClass} ${reachedClass}`}>
              {(stage.type === StageTypes.SYSTEM_DECLINED) && (
                <div className="ca-gapp-checks-stage-top" key={stage.id}>
                  <div className={`ca-gapp-checks-stage-top-num`} style={{borderColor: stage.color}}></div>
                  <div className="ca-gapp-checks-stage-top-title">{stage.title}</div>
                </div>
              )}
              {stage.tasks.map((task) => {
                const assigns = this.getAssignments(task);
                // const neededCount = isCurrent ? Math.max(task.assCount - assigns.length, 0) : 0;
                const neededCount = isPast ? 0 : Math.max(task.assCount - assigns.length, 0);
                const neededList = new Array(neededCount).fill(42);
                const completedAssCount = assigns.filter(ass => !!ass.completedTs).length;
                const isTaskComplete = completedAssCount >= task.assCount;
                return (
                  <React.Fragment key={task.id}>
                    <div className="ca-gapp-checks-stage-top">
                      <div className={`ca-gapp-checks-stage-top-num ${isTaskComplete ? 'complete' : ''}`} style={{borderColor: stage.color}}>
                        {isTaskComplete && <Icon.CheckCircle1 style={{color: stage.color}} />}
                      </div>
                      <div className="ca-gapp-checks-stage-top-title">{stage.title}</div>
                      <div className="ca-gapp-checks-stage-top-subtitle">{task.title}</div>
                    </div>
                    <div className="ca-gapp-checks-task">
                      {/* <div className="ca-gapp-checks-task-top"> */}
                      {/*   <div className="ca-gapp-checks-task-top-title">{task.title}</div> */}
                      {/* </div> */}
                      <div className="ca-gapp-checks-task-todos">
                        {assigns.map((ass) => {
                          const emp = ass.employee;
                          const name = emp ? `${emp.firstName} ${emp.lastName}` : 'Employee';
                          const isComplete = !!ass.completedTs;
                          const statusClass = isComplete ? 'complete' : 'assigned';
                          const resultVal = isComplete
                            ? ((ass.result || {})[ass.resultTaskDetails?.type]) || null
                            : null;
                          const resultTaskType = ass.resultTaskDetails?.type || null;
                          const res = `${resultTaskType} ${resultVal}`;
                          const indStyle = {borderColor: stage.color};
                          if (isComplete) indStyle.backgroundColor = stage.color;
                          return (
                            <div key={ass.id} className={`ca-gapp-checks-task-todo ${statusClass}`}>
                              <div className="ca-gapp-checks-task-todo-menu-con">
                                {!isComplete && (
                                  <EllipsisMenu usePortal left>
                                    <a href="#" onClick={this.onClickDeleteAssignment.bind(this, ass, emp, task)}>Remove</a>
                                  </EllipsisMenu>
                                )}
                              </div>
                              <div className="ca-gapp-checks-task-todo-ind" style={indStyle} />
                              <div className="ca-gapp-checks-task-todo-name">{name}</div>
                              {renderAssResultChip(ass)}
                            </div>
                          );
                        })}
                        {neededList.map((_foo, i) => {
                          const statusClass = isCurrent ? 'unassigned' : 'future';
                          return (
                            <div key={i} className={`ca-gapp-checks-task-todo ${statusClass}`}>
                              <div className="ca-gapp-checks-task-todo-menu-con">
                                {/* {!!isCurrent && ( */}
                                {/*   <EllipsisMenu usePortal left> */}
                                {/*     <a href="#" onClick={this.onClickAssigneesModal.bind(this, stage, task)}>Assign</a> */}
                                {/*   </EllipsisMenu> */}
                                {/* )} */}
                              </div>
                              <div className="ca-gapp-checks-task-todo-ind" />
                              <div className="ca-gapp-checks-task-todo-name">Unassigned</div>
                            </div>
                          );
                        })}
                        {(isCurrent && !isTaskComplete) && (
                          <div className="ca-gapp-checks-task-ctas">
                            <button className="btn xs secondary blue" onClick={this.onClickAssigneesModal.bind(this, stage, task)}>Add Assignees</button>
                            <button className="btn xs secondary blue" onClick={this.onClickAddMyself.bind(this, stage, task)} disabled={this.state.addSelfPending}>{this.state.addSelfPending ? 'Assigning...' : 'Complete Task'}</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <div className="ca-gapp-checks">
        <h1 className="ca-gapp-checks-title">Workflow</h1>
        {this.renderWorkflow()}
        {this.renderAssigneesModal()}
      </div>
    );
  }

}

CadminGrantAppChecklist.propTypes = {
  app: PropTypes.object,
  company: PropTypes.object,
  workflow: PropTypes.object,
  assignments: PropTypes.arrayOf(PropTypes.object),
};

CadminGrantAppChecklist.defaultProps = {
};

const stateToProps = (state) => ({
  currentUser: AuthSlx.currentUser(state),
});

const dispatchToProps = (dispatch, ownProps) => ({
  createAssignments: (taskId, employeeIds) => dispatch(GrantSysDuck.Ax.assignmentsCreateMany(ownProps.app.id, {taskId, employeeIds})),
  toastSuccess: (msg) => dispatch(ToastAx.success(msg)),
  deleteAssignment: (assId) => dispatch(GrantSysDuck.Ax.assignmentsDelete(assId)),
});

export default connect(stateToProps, dispatchToProps)(CadminGrantAppChecklist);
